import { useState } from "react";
import PropTypes from "prop-types";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { userApi } from "../../services/api";
import { displayName } from "../tasks/userDisplay";

/**
 * The "Specific people" list (household sharing D2), for anything shared
 * that way — projects, drawings. Add a person by their exact username or
 * email (never a browsable list of accounts); remove with the chip's ×.
 * Anyone added here is sent by what was typed (`via`) — see sharePayload.
 * Read-only unless `canEdit`: only a person may change who it reaches.
 */
function PeoplePicker({
  idPrefix,
  people,
  onChange,
  canEdit = false,
  serverError = null,
  readOnlyReason = "Only its owner can change who it's shared with.",
}) {
  const [who, setWho] = useState("");
  const [whoError, setWhoError] = useState(null);
  const [finding, setFinding] = useState(false);

  // Find one person by their exact username or email and add them. The
  // server answers 404 for anything that isn't exactly a person here, so
  // the message says what to check rather than guessing.
  const addPerson = async () => {
    const q = who.trim();
    if (!q || finding) return;
    setFinding(true);
    setWhoError(null);
    try {
      const { data } = await userApi.lookup(q);
      if (people.some((p) => p.id === data.id)) {
        setWhoError(`${data.username} is already on the list.`);
      } else {
        onChange([...people, { id: data.id, username: data.username, via: q }]);
        setWho("");
      }
    } catch (err) {
      setWhoError(
        err?.response?.status === 404
          ? "No one here has exactly that username or email — check the spelling."
          : err?.response?.status === 429
            ? "That's a lot of look-ups — try again in a while."
            : "Couldn't look that up right now. Try again.",
      );
    } finally {
      setFinding(false);
    }
  };

  const inputId = `${idPrefix}-share-who`;
  const errorId = `${idPrefix}-share-who-error`;

  return (
    <div className="grid gap-2">
      {canEdit ? (
        <>
          <Label htmlFor={inputId}>
            Add a person by their exact username or email
          </Label>
          <div className="flex gap-2">
            <Input
              id={inputId}
              value={who}
              onChange={(e) => setWho(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addPerson();
                }
              }}
              placeholder="username or email"
              autoComplete="off"
              aria-invalid={Boolean(whoError)}
              aria-describedby={whoError ? errorId : undefined}
            />
            <Button
              type="button"
              variant="secondary"
              className="h-11 md:h-9"
              onClick={addPerson}
              disabled={!who.trim() || finding}
            >
              {finding ? <Loader2 className="animate-spin" /> : "Add"}
            </Button>
          </div>
          {whoError && (
            <p id={errorId} className="text-sm text-destructive">
              {whoError}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{readOnlyReason}</p>
      )}
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nobody yet — until someone is added, only its owner and their agents
          see it.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2" aria-label="Shared with">
          {people.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-sm"
            >
              {displayName(p)}
              {canEdit && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="rounded-full p-0.5 hover:bg-accent"
                    aria-label={`Remove ${displayName(p)}`}
                    onClick={() =>
                      onChange(people.filter((x) => x.id !== p.id))
                    }
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {serverError && <p className="text-sm text-destructive">{serverError}</p>}
    </div>
  );
}

PeoplePicker.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  people: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      username: PropTypes.string.isRequired,
      via: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  serverError: PropTypes.string,
  readOnlyReason: PropTypes.string,
};

export default PeoplePicker;
